import React from 'react';
import Layout from '../components/Layout';
import { Mail, CheckCircle2 } from 'lucide-react';

const SLA = () => {
    return (
        <Layout>
            <section className="py-20 bg-white dark:bg-slate-950 transition-colors duration-500">
                <div className="container mx-auto px-4 max-w-4xl">
                    
                    {/* Header */}
                    <div className="mb-12">
                        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
                            Service Level <span className="text-accent">Agreement</span>
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400">
                            Last updated: Tue 3 Feb, 2026
                        </p>
                    </div>

                    {/* Content */}
                    <div className="prose prose-slate dark:prose-invert max-w-none">
                        <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
                            This SLA outlines service availability objectives and support principles.
                        </p>

                        <SLASection title="Service Availability Objective">
                            <p>
                                EverestCloud targets high service availability for its core infrastructure and platform services. Availability objectives are defined per service and deployment model.
                            </p>
                            <p>Availability may be affected by:</p>
                            <ul>
                                <li>Scheduled maintenance</li>
                                <li>Customer-controlled configurations</li>
                                <li>Force majeure events</li>
                                <li>External connectivity dependencies</li>
                            </ul>
                        </SLASection>

                        <SLASection title="Maintenance">
                            <p>
                                Planned maintenance will be communicated in advance wherever reasonably possible.
                            </p>
                        </SLASection>

                        <SLASection title="Incident Management">
                            <p>EverestCloud operates a multi-tier support model:</p>
                            <ul>
                                <li>Incident logging and classification</li>
                                <li>Progressive escalation based on severity</li>
                                <li>Continuous monitoring and resolution tracking</li>
                            </ul>
                        </SLASection>

                        <SLASection title="Support Response Targets">
                            <p>Indicative response objectives (subject to contract):</p>
                            <div className="grid md:grid-cols-3 gap-4 mt-4 not-prose">
                                <ResponseCard level="Critical" response="Rapid response" color="red" />
                                <ResponseCard level="High Priority" response="Priority handling" color="orange" />
                                <ResponseCard level="Standard" response="Best-effort response" color="blue" />
                            </div>
                        </SLASection>

                        <SLASection title="Service Credits">
                            <p>
                                Service credits, if applicable, are governed by the commercial agreement and are not automatic.
                            </p>
                        </SLASection>

                        <SLASection title="Exclusions">
                            <p>SLA does not apply to:</p>
                            <ul>
                                <li>Customer misconfiguration</li>
                                <li>Third-party software failures</li>
                                <li>Unsupported usage scenarios</li>
                                <li>Force majeure events</li>
                            </ul>
                        </SLASection>

                        <SLASection title="Review & Updates">
                            <p>
                                This SLA may evolve as services mature. Contractual SLAs take precedence over website content.
                            </p>
                        </SLASection>

                        {/* Contact */}
                        <div className="mt-12 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-accent/10 rounded-lg">
                                    <Mail className="w-5 h-5 text-accent" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">Contact</p>
                                    <a href="mailto:info@everestcloud.com.np" className="text-accent hover:underline font-medium">
                                        info@everestcloud.com.np
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

const SLASection = ({ title, children }) => (
    <div className="mb-8">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
            {title}
        </h2>
        <div className="text-slate-600 dark:text-slate-300 leading-relaxed space-y-3 pl-4">
            {children}
        </div>
    </div>
);

const ResponseCard = ({ level, response, color }) => {
    const colors = {
        red: 'border-red-500/30 bg-red-500/5',
        orange: 'border-orange-500/30 bg-orange-500/5',
        blue: 'border-blue-500/30 bg-blue-500/5'
    };
    const dotColors = {
        red: 'bg-red-500',
        orange: 'bg-orange-500',
        blue: 'bg-blue-500'
    };
    
    return (
        <div className={`p-4 rounded-lg border ${colors[color]}`}>
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${dotColors[color]}`}></div>
                <span className="font-bold text-slate-900 dark:text-white text-sm">{level}</span>
            </div>
            <p className="text-slate-600 dark:text-slate-400 text-sm">{response}</p>
        </div>
    );
};

export default SLA;
